import { Component, computed, effect, inject, input, untracked } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractManageSurveyComponent } from '@unity/abstract/abstract-manage-survey.component';
import {
  FormDataDto,
  FormInstancesClient,
  FormResponseDetailedDto,
  FormResponsesClient,
  TransitionMiniDto,
  TransitionRequestDto,
} from '@unity/clients';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { SurveyDataService } from '@unity/services/survey-data.service';
import { ActionDeleteSharedComponent } from '@unity/shared/composed/action-delete-shared/action-delete-shared.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { CardManageSurveySharedComponent } from '@unity/shared/composed/card-manage-survey-shared/card-manage-survey-shared.component';
import { WorkflowSelectorSharedComponent } from '@unity/shared/workflow-selector-shared/workflow-selector-shared.component';
import { QuickActions } from '@unity/utils/quick-actions';
import { UnityEntitlementPath } from '@unity/utils/types';
import { EMPTY } from 'rxjs';

@Component({
  selector: 'unity-manage-form-response-page',
  imports: [
    KENDO_STACKLAYOUT,
    KENDO_BUTTON,
    ReactiveFormsModule,
    PageContainerComponent,
    AuthorizeDirective,
    ActionDeleteSharedComponent,
    WorkflowSelectorSharedComponent,
    ActionSharedComponent,
    CardManageSurveySharedComponent,
  ],
  templateUrl: './manage-form-response-page.component.html',
  styleUrl: './manage-form-response-page.component.scss',
})
export class ManageFormResponsePageComponent extends AbstractManageSurveyComponent<FormResponseDetailedDto> {
  private readonly formResponsesClient = inject(FormResponsesClient);
  private readonly formInstancesClient = inject(FormInstancesClient);
  private readonly surveyDataService = inject(SurveyDataService);

  /* Inputs from Route */
  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });
  public readonly $formResponseId = input.required({ alias: 'formResponseId', transform: (value: string) => parseInt(value) });

  protected readonly $returnToAgreementAction = computed(() => QuickActions.ReturnToAgreement(this.$agreementId()));

  /* Impl */
  protected override mainResource = rxResource({
    params: () => {
      const resourceId = this.$resourceId();
      const agreementId = this.$agreementId();
      return agreementId && resourceId ? { agreementId, resourceId } : undefined;
    },
    stream: ({ params: { agreementId, resourceId } }) => this.formResponsesClient.getOneForAgreementAsDetailed(agreementId, resourceId),
  });

  protected override readonly $formSchema = computed(() => {
    const resource = this.$resource();
    return resource?.form_schema;
  });

  protected override updateResourceFn$ = (resourceId: number, data: FormDataDto, transition?: TransitionMiniDto) => {
    const agreementId = untracked(this.$agreementId);
    const formInstance = this.$formInstance();

    if (!formInstance) {
      return EMPTY;
    }

    return this.formResponsesClient.updateOneForAgreement(agreementId, resourceId, {
      agreement_id: agreementId,
      transition_id: transition?.id ?? null,
      data: data,
      form_schedule_id: formInstance.form_schedule.id,
    });
  };

  protected override getData(resource: FormResponseDetailedDto): FormDataDto {
    return resource.data;
  }

  protected override deleteResourceFn$ = (resourceId: number) => {
    const agreementId = this.$agreementId();
    if (!agreementId) {
      return EMPTY;
    }
    return this.formResponsesClient.deleteOneForAgreement(agreementId, resourceId);
  };

  protected override editEntitlement: UnityEntitlementPath = 'FormResponse:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'FormResponse:Transition';
  protected override deleteEntitlement: UnityEntitlementPath = 'FormResponse:Delete';
  protected override resourceName = 'Form';
  protected override $resourceId = this.$formResponseId;

  protected override availableTransitionsFn$ = (_resourceId: number) => {
    const agreementId = this.$agreementId();
    return this.availableTransitionsClient.getAllForFormResponseAsMini(agreementId, _resourceId);
  };

  protected override doTransitionFn$ = (_resourceId: number, transition: TransitionRequestDto) => {
    const agreementId = this.$agreementId();
    return this.transitionsClient.setForFormResponse(agreementId, _resourceId, transition);
  };

  /* Resources */
  private readonly formInstanceResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      const formResponse = this.mainResource.value();
      const formInstanceId = formResponse?.form_instance.id;
      return agreementId && formInstanceId ? { agreementId, formInstanceId } : undefined;
    },
    stream: ({ params: { agreementId, formInstanceId } }) =>
      this.formInstancesClient.getOneForAgreementAsDetailed(agreementId, formInstanceId),
  });

  /* Computed */
  protected readonly $formSchemaName = computed(() => {
    const formInstance = this.$formInstance();
    return formInstance?.form_schedule.name ?? '';
  });

  protected readonly $contentHeader = computed(() => {
    const formInstance = this.$formInstance();
    return `${formInstance?.form_schedule.name}: ${formInstance?.id}`;
  });

  protected readonly $contentHeaderSecondary = computed(() => {
    const formInstance = this.$formInstance();
    return `Agreement: ${formInstance?.agreement.name}`;
  });

  protected readonly $formInstance = computed(() => {
    if (this.formInstanceResource.hasValue()) {
      return this.formInstanceResource.value();
    }
    return undefined;
  });

  /* Data */
  public constructor() {
    super();

    effect(() => {
      const agreementId = this.$agreementId();
      this.surveyDataService.setAgreementId(agreementId);
    });
  }
}
